// Column definitions with metadata
export interface ColumnDefinition {
  id: string;
  label: string;
  category: 'core' | 'technical' | 'options' | 'fundamentals' | 'indicators';
  isDefault: boolean;
  sortable: boolean;
  format?: 'currency' | 'percentage' | 'number' | 'text' | 'score' | 'volume';
  width?: string;
  description?: string;
}

export const COLUMN_DEFINITIONS: ColumnDefinition[] = [
  // Default columns (always visible)
  { id: 'symbol', label: 'Symbol', category: 'core', isDefault: true, sortable: true, format: 'text', width: '80px' },
  { id: 'company_name', label: 'Company', category: 'core', isDefault: true, sortable: true, format: 'text', width: '150px' },
  { id: 'cc_score', label: 'Babylon Rating', category: 'core', isDefault: true, sortable: true, format: 'score', width: '110px' },
  { id: 'current_price', label: 'Price', category: 'core', isDefault: true, sortable: true, format: 'currency', width: '90px' },
  { id: 'best_ann_yield', label: 'Ann. Yield', category: 'core', isDefault: true, sortable: true, format: 'percentage', width: '100px', description: 'Best annualized yield from 30-45 DTE calls' },
  { id: 'iv', label: 'IV', category: 'core', isDefault: true, sortable: true, format: 'percentage', width: '70px', description: 'Current implied volatility of the best option' },
  { id: 'iv_rank', label: 'IV Rank', category: 'core', isDefault: true, sortable: true, format: 'number', width: '80px', description: 'IV percentile rank vs 252-day history (0-100)' },
  { id: 'rsi', label: 'RSI', category: 'core', isDefault: true, sortable: true, format: 'number', width: '70px' },
  { id: 'sector', label: 'Sector', category: 'core', isDefault: true, sortable: true, format: 'text', width: '100px' },
  { id: 'market_cap', label: 'Market Cap', category: 'core', isDefault: true, sortable: true, format: 'text', width: '100px' },
  
  // Optional columns - Technical Analysis
  { id: 'ma_20', label: '20-day MA', category: 'technical', isDefault: false, sortable: true, format: 'currency', width: '90px' },
  { id: 'price_vs_ma', label: 'Price vs MA', category: 'technical', isDefault: false, sortable: true, format: 'percentage', width: '100px' },
  { id: 'volume', label: 'Volume (M)', category: 'technical', isDefault: false, sortable: true, format: 'volume', width: '100px' },
  
  // Optional columns - Options Details
  { id: 'best_dte', label: 'DTE', category: 'options', isDefault: true, sortable: true, format: 'number', width: '70px', description: 'Days to expiration of the best option candidate (theta proxy — shorter DTE = faster decay)' },
  { id: 'bid_ask_spread', label: 'Spread %', category: 'options', isDefault: true, sortable: true, format: 'percentage', width: '90px', description: 'Bid-ask spread % — execution cost. Lower is better.' },
  { id: 'avg_call_volume', label: 'Call Vol (M)', category: 'options', isDefault: false, sortable: true, format: 'volume', width: '100px' },
  { id: 'best_call_oi', label: 'Max Call OI', category: 'options', isDefault: false, sortable: true, format: 'number', width: '100px', description: 'Maximum Open Interest among viable options (30-45 DTE, 0.15-0.30 delta, OTM)' },
  
  // Optional columns - Fundamentals
  { id: 'dividend_yield', label: 'Div. Yield', category: 'fundamentals', isDefault: false, sortable: true, format: 'percentage', width: '90px' },
  { id: 'cc_actual_net_margin', label: 'Net Margin', category: 'fundamentals', isDefault: false, sortable: true, format: 'percentage', width: '100px', description: 'TTM Net Income / Revenue' },
  { id: 'cc_actual_debt_equity', label: 'Debt/Equity', category: 'fundamentals', isDefault: false, sortable: true, format: 'number', width: '100px', description: 'Total Debt / Total Equity ratio' },
  
  // Optional columns - Indicators
  { id: 'options_available', label: 'Options', category: 'indicators', isDefault: false, sortable: true, format: 'text', width: '80px' },
  { id: 'next_earnings_date', label: 'Next Earnings', category: 'indicators', isDefault: true, sortable: true, format: 'text', width: '120px', description: 'Date of next earnings announcement for option strategy timing' },
  { id: 'cc_cluster_setup_label', label: 'Setup', category: 'indicators', isDefault: true, sortable: true, format: 'text', width: '90px', description: 'Cluster-Setup engine output: good / neutral / risk based on 489-stock backtest of (cluster × cell)' },
];

/** One cap of the default score (gold.stock_score.cap_reason), named after its rule. */
export interface CapEntry {
  rule: string;  // liquidity | net_margin | earnings_within_30d | losing_setup | leveraged_fund
  dimension: 'income_fit' | 'timing' | null;
  cap: number | null;
  /** The rule's own name, e.g. "Earnings within 30 days" (never "inside the call"). */
  label: string;
  /** e.g. "spread 61%", "open interest 5", "earnings in 23d" (read-time). */
  detail: string | null;
}

/** Liquidity facts of the side traded, against the rule the server runs (v1 or v2). */
export interface LiquidityFacts {
  pass: boolean;
  /** spread_unknown | spread_above_max | oi_unknown | oi_below_min | volume_unknown | volume_below_min */
  reasons: string[];
  spread_pct: number | null;
  max_oi: number | null;
  avg_volume: number | null;
  rule: { version: 'v1' | 'v2'; max_spread_pct: number; min_open_interest: number; min_stock_volume: number };
}

/** One screener row as served by GET /api/stocks/screener (gold phase 2b:
 *  gold.v_screener). Every figure is computed by the backend for the lens
 *  requested (CC or CSP); null = unknown, shown "—". The browser formats only. */
export interface StockScreenerData {
  symbol: string;
  strategy?: 'cc' | 'csp';
  company_name: string | null;
  sector: string | null;
  market_cap: number | null;        // USD
  is_fund?: boolean | null;
  is_leveraged_fund?: boolean | null;
  ticker_type?: string | null;
  // Price (gold.stock_mark)
  current_price: number | null;
  change_percent?: number | null;
  price_kind?: string | null;
  price_session_date?: string | null;
  price_as_of?: string | null;
  // Score of the lens (gold.stock_score)
  score: number | null;              // null = not scored
  income_fit: number | null;
  timing: number | null;
  scored_at?: string | null;
  caps?: CapEntry[];
  income_fit_caps?: CapEntry[];
  timing_caps?: CapEntry[];
  not_scored?: boolean;
  no_standard_put?: boolean;
  passes_defaults?: boolean | null;
  // Best contract (glossary best_contract; premium yield in % per year)
  best_contract?: string | null;
  best_ann_yield: number | null;
  best_dte: number | null;
  best_delta: number | null;         // shown positive
  best_strike?: number | null;
  best_expiry?: string | null;
  best_premium?: number | null;
  // Earnings (New York date, read-time)
  next_earnings_date: string | null; // YYYY-MM-DD
  days_to_earnings: number | null;
  earnings_inside: boolean | null;   // on or before the best contract's expiry
  // Volatility
  iv: number | null;                 // stock IV (IV30), %
  hv: number | null;                 // 20-day realized, %
  iv_hv_ratio?: number | null;
  iv_rank: number | null;            // 0-100
  iv_rank_since?: string | null;
  iv_rank_window_sessions?: number | null;
  iv_rank_interim?: boolean | null;  // true = "IV rank (since …)"
  // Liquidity of the side traded (stock level)
  spread_pct: number | null;
  max_oi: number | null;
  avg_volume?: number | null;
  liquidity?: LiquidityFacts;
  // Fundamentals (blank for funds and when unknown; 0 shown as 0)
  net_margin: number | null;         // %
  debt_to_equity: number | null;
  dividend_yield: number | null;     // %
  trend?: string | null;             // bullish | bearish | neutral
  // Setup (call history)
  setup_cell: string | null;
  setup_pnl: number | null;          // % per 35 days
  setup_basis: 'stock' | 'cluster' | null;
  setup_n: number | null;
  baseline_pnl: number | null;       // usual result, % per 35 days
  setup_vs_usual: number | null;     // points
  setup_share: number | null;        // %
  setup_frequency: 'frequent' | 'occasional' | 'rare' | null;

  // ---- Deprecated: stock_screener_aggregate fields, no longer sent (phase 2b).
  // Optional, kept only so components that are no longer mounted still compile.
  cc_score?: number | null;
  rsi?: number;
  ma_20?: number;
  price_vs_ma?: number;
  volume?: number;
  prev_close?: number;
  cc_cap_reason?: string;
  csp_cap_reason?: string;
  avg_call_volume?: number;
  best_call_oi?: number;
  bid_ask_spread?: number;
  market_cap_value?: number;
  options_available?: boolean;
  data_source?: string;
  credit_rating?: string;

  // CC Score Dimension Scores (v3 — 2-dimension framework)
  cc_dimension1_score?: number;  // Dimension 1: Is It a Good CC Stock? (0-50)
  cc_dimension2_score?: number;  // Dimension 2: Is It a Good Time? (0-50)

  // CC Score Sub-filter Scores (new names)
  cc_quality_score?: number;            // 1A Quality Filter (0-20)
  cc_yield_potential_score?: number;    // 1B Yield (0-10)
  cc_liquidity_filter_score?: number;  // 1C Liquidity (0-20)
  cc_premium_richness_score?: number;   // 2A Premium Richness (0-20)
  cc_stock_condition_score?: number;    // 2B Stock Condition (0-15)
  cc_calendar_risk_score?: number;      // 2C Calendar Risk (0-15)

  // Legacy CC Score Components (backward compatibility)
  cc_liquidity_score?: number;
  cc_iv_rank_score?: number;
  cc_technical_score?: number;
  cc_earnings_risk_score?: number;

  // CC Score Actual Values for Educational Display
  cc_actual_max_oi?: number;
  cc_actual_spread_pct?: number;
  cc_actual_stock_volume?: number;
  cc_actual_avg_iv?: number;
  iv_min?: number;
  iv_max?: number;
  cc_actual_trend_signal?: string;
  cc_actual_slope_12w?: number;
  cc_actual_return_zscore?: number;
  cc_parabolic_flag?: string;
  cc_actual_rsi?: number;
  cc_actual_net_margin?: number;
  cc_actual_debt_equity?: number;
  cc_earnings_days_info?: string;

  // MACD Momentum Confirmation
  cc_macd_hist_rising?: boolean;
  cc_macd_bull_cross?: boolean;
  cc_macd_bonus?: number;

  // Yield Score + Consolidation Detection
  cc_yield_score?: number;              // Annualized yield score (0-10)
  cc_consolidation_zscore?: number;     // Consolidation z-score value
  cc_consolidation_bonus?: number;      // Consolidation bonus (0, 1, 2, or 4)

  // VRP (Volatility Risk Premium)
  cc_actual_hv_20?: number;             // 20-day historical volatility (annualized %)
  cc_vrp_score?: number;                // VRP sub-score (0-8)
  cc_iv_rank_sub_score?: number;        // IV Rank sub-score (0-12)

  // v7.0 Cluster-Setup framework fields
  cc_baseline_pnl_pct?: number;                 // Per-symbol 35-day CC mean P&L %
  cc_cluster_id?: number;                        // 1..12 behavioral cluster
  cc_current_cell?: string;                      // 3x3x3 cell, e.g. "LO_FLAT_TIGHT"
  cc_cluster_setup_label?: 'strong' | 'positive' | 'marginal' | 'negative' | 'good' | 'neutral' | 'risk' | 'unknown';
  cc_cluster_setup_delta?: number;              // Cluster-cell delta P&L (pp)
  cc_cluster_setup_score?: number;              // Dim 2B score (0..20)
  cc_stock_cell_delta?: number;                 // Stock's own delta in current cell (pp)
  cc_stock_cell_n?: number;                     // Stock's N in current cell
  cc_setup_total_n?: number | null;            // Sum of N over all the stock's cells (for "how often")

  // CSP Score (Cash-Secured Put) — mirrors CC v7.2 via put-call parity.
  // 1A baseline / 2A setup / 2B calendar reuse the shared cc_* facts above;
  // only the yield + put transparency differ. NULL when no viable put.
  csp_score?: number;
  csp_dimension1_score?: number;                // 1A Baseline (25) + 1B Put Yield (25)
  csp_dimension2_score?: number;                // 2A Setup (35) + 2B Calendar (15)
  csp_quality_score?: number;                   // 1A Baseline P&L score (0-25)
  csp_yield_potential_score?: number;           // 1B Put Yield score (0-25)
  csp_entry_setup_score?: number;               // 2A cluster-setup score (0-35)
  csp_calendar_risk_score?: number;             // 2B Calendar Risk (0-15)
  csp_best_dte?: number;
  csp_best_ann_yield?: number;                  // Best annualized put yield %
  csp_best_put_oi?: number;
  csp_avg_put_volume?: number;
  csp_actual_max_put_oi?: number;
  csp_actual_put_spread_pct?: number;

  // Custom framework scoring breakdown (present when framework_id is used)
  score_breakdown?: {
    total_score: number;
    quality_score: number;
    timing_score: number;
    quality_max: number;
    timing_max: number;
    indicator_breakdown: Array<{
      indicator_key: string;
      display_name: string;
      dimension: string;
      raw_value: number | null;
      score_pct: number;
      points_earned: number;
      max_points: number;
      matched_label: string;
    }>;
    premium_breakdown: Array<{
      module_key: string;
      display_name: string;
      raw_score: number;
      normalized_score: number;
      points_earned: number;
      max_points: number;
    }>;
    framework_id: number;
    framework_name: string;
  };
}